import { useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { X, Lock, CaretDown } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useNavBadges } from "@/lib/queries";
import { IdleTimer } from "@/components/IdleTimer";
import { TopBar } from "@/components/TopBar";
import { CommandPalette, useCommandPaletteShortcut } from "@/components/CommandPalette";
import { WorkspaceNavPanel } from "@/components/WorkspaceNavPanel";
import { AdminNavPanel } from "@/components/AdminNavPanel";
import { HubNavPanel } from "@/components/HubNavPanel";
import { BreadcrumbProvider } from "@/lib/breadcrumb";
import { KasaraWidget } from "@/components/kasara/KasaraWidget";
import { PortalTrustFooter } from "@/components/PortalTrustFooter";
import { NAV_GROUPS, clientNavGroups } from "@/lib/nav";
import { isWorkspacePath } from "@/lib/workspaceNav";
import { hubFromPath } from "@/lib/hubNav";
import { TokuroLogo, TokuroMark } from "@/components/brand/TokuroLogo";
import { cn } from "@/lib/utils";

function NavBadge({ count, tone, routeName }) {
  if (!count) return null;
  const display = count > 99 ? "99+" : String(count);
  return (
    <span
      data-testid={`sidebar-badge-${routeName}`}
      aria-label={`${count} pending items`}
      className={cn(
        "ml-auto flex h-4 min-w-[16px] items-center justify-center rounded-[2px] px-1 text-[9px] font-mono font-bold leading-none tracking-tight tabular-nums",
        tone === "attention"
          ? "bg-destructive text-white"
          : "border border-border bg-secondary text-muted-foreground"
      )}
    >
      {display}
    </span>
  );
}

function NavItem({ item, count, collapsed, onNavigate }) {
  const { icon: Icon, label, to, key, ai, badgeTone } = item;
  return (
    <NavLink
      to={to}
      end={to === "/"}
      onClick={onNavigate}
      title={collapsed ? label : undefined}
      data-testid={`nav-${key}`}
      className={({ isActive }) =>
        cn(
          "group relative mx-2 flex items-center rounded-[2px] text-sm font-medium transition-all duration-200 ease-out",
          collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2",
          isActive
            ? "bg-card font-semibold text-foreground shadow-sm ring-1 ring-border"
            : "text-sidebar-muted hover:bg-sidebar-hover/70 hover:text-sidebar-foreground"
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={cn("h-[18px] w-[18px] shrink-0 transition-colors", ai ? "text-accent-ai" : isActive ? "text-accent-ai" : "text-sidebar-muted group-hover:text-sidebar-foreground")} weight={isActive ? "fill" : "bold"} />
          {!collapsed && <span>{label}</span>}
          {!collapsed && ai && !isActive && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-accent-ai/70" />}
          {!collapsed && <NavBadge count={count} tone={badgeTone} routeName={key} />}
          {collapsed && count > 0 && (
            <>
              <span aria-hidden="true" className={cn("absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full", badgeTone === "attention" ? "bg-destructive" : "bg-accent-ai/70")} />
              <span className="sr-only">{count} pending</span>
            </>
          )}
        </>
      )}
    </NavLink>
  );
}

function ComingSoonItem({ item }) {
  const { icon: Icon, label, key } = item;
  return (
    <div
      data-testid={`nav-soon-${key}`}
      className="mx-2 flex cursor-not-allowed items-center gap-3 border-l-[3px] border-transparent px-3 py-2 text-sm text-sidebar-muted/45"
      title="Coming soon"
    >
      <Icon className="h-[18px] w-[18px] shrink-0" weight="regular" />
      <span className="flex-1">{label}</span>
      <Lock className="h-3 w-3" weight="bold" />
    </div>
  );
}

function NavGroup({ group, badges, collapsed, onNavigate }) {
  const { can } = useAuth();
  const [groupCollapsed, setGroupCollapsed] = useState(false);
  const visible = useMemo(
    () => group.items.filter((i) => i.comingSoon || can(i.perm)),
    [group.items, can]
  );
  if (visible.length === 0) return null;

  return (
    <div className="py-2">
      {!collapsed && (
        <button
          onClick={() => setGroupCollapsed((v) => !v)}
          data-testid={`nav-group-${group.key}`}
          className="flex w-full items-center justify-between px-5 pb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-shell-muted/70 transition-colors hover:text-shell-text"
          aria-expanded={!groupCollapsed}
        >
          {group.label}
          <CaretDown className={cn("h-3 w-3 transition-transform", groupCollapsed && "-rotate-90")} weight="bold" />
        </button>
      )}
      {(collapsed || !groupCollapsed) && (
        <div className="space-y-0.5">
          {visible.map((item) =>
            item.comingSoon
              ? <ComingSoonItem key={item.key} item={item} />
              : <NavItem key={item.key} item={item} count={item.badge ? badges?.[item.badge] : 0} collapsed={collapsed} onNavigate={onNavigate} />
          )}
        </div>
      )}
    </div>
  );
}

function SidebarContent({ onNavigate, onOpenPalette, collapsed = false, onToggleCollapsed, groups = NAV_GROUPS, showPalette = true, workspaceNav = false, adminNav = false, hub = null }) {
  const { data: badges } = useNavBadges();
  const logoInner = collapsed ? (
    <TokuroMark heightPx={28} variant="dark" />
  ) : (
    <TokuroLogo variant="dark" size="md" showTagline testId="sidebar-brand-logo" />
  );
  return (
    <div className="shell-grain flex h-full flex-col bg-shell-base">
      {onToggleCollapsed ? (
        <button type="button" onClick={onToggleCollapsed} data-testid="sidebar-logo-toggle"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"} aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={cn("group flex items-center border-b border-shell-border transition-colors hover:bg-shell-surface/50", collapsed ? "justify-center px-2 py-[18px]" : "justify-start px-5 py-[18px]")}>
          {logoInner}
        </button>
      ) : (
        <NavLink to="/" onClick={onNavigate} data-testid="sidebar-logo-home"
          className={cn("flex items-center border-b border-shell-border", collapsed ? "justify-center px-2 py-[18px]" : "justify-start px-5 py-[18px]")}>
          {logoInner}
        </NavLink>
      )}

      {workspaceNav ? (
        <div className="min-h-0 flex-1">
          <WorkspaceNavPanel collapsed={collapsed} onNavigate={onNavigate} />
        </div>
      ) : adminNav ? (
        <div className="min-h-0 flex-1">
          <AdminNavPanel collapsed={collapsed} onNavigate={onNavigate} />
        </div>
      ) : hub ? (
        <div className="min-h-0 flex-1">
          <HubNavPanel hub={hub} collapsed={collapsed} onNavigate={onNavigate} />
        </div>
      ) : (
        <nav className="flex-1 divide-y divide-shell-border/60 overflow-y-auto py-1" aria-label="Primary">
          {groups.map((group) => (
            <NavGroup key={group.key} group={group} badges={badges} collapsed={collapsed} onNavigate={onNavigate} />
          ))}
        </nav>
      )}

      <div className={cn("border-t border-shell-border py-3", collapsed ? "px-2" : "px-3")}>
        {collapsed ? (
          <div className="flex justify-center" title="Secure · RBAC · Audited">
            <span className="h-1.5 w-1.5 rounded-full bg-success pulse-ai" />
          </div>
        ) : (
          <p className="flex items-center gap-1.5 px-2 font-mono text-[9px] uppercase tracking-[0.2em] text-shell-muted/70">
            <span className="h-1.5 w-1.5 rounded-full bg-success pulse-ai" /> Secure · RBAC · Audited
          </p>
        )}
      </div>
    </div>
  );
}

export default function AppLayout({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const isClient = user?.user_type === "client";
  const navGroups = isClient ? clientNavGroups() : NAV_GROUPS;
  // Context-aware shell: a firm user inside a client workspace gets the workspace navigation
  // in place of the global firm nav.
  const inWorkspace = !isClient && isWorkspacePath(location.pathname);
  // Admin console gets the same numbered-stage nav treatment as client workspaces.
  const inAdmin = !isClient && /^\/admin(\/|$)/.test(location.pathname);
  // Hub sidebar transformation for Studio · Insights · Knowledge Hub · Compliance Reference.
  // Same "in-sidebar sub-nav with BACK to main menu" pattern as the client workspace.
  const hub = !isClient && !inWorkspace && !inAdmin ? hubFromPath(location.pathname) : null;
  const [navOpen, setNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("daakyi.sidebar.collapsed") === "1"; } catch { return false; }
  });
  const toggleCollapsed = () => setCollapsed((v) => {
    const next = !v;
    try { localStorage.setItem("daakyi.sidebar.collapsed", next ? "1" : "0"); } catch { /* ignore */ }
    return next;
  });
  useCommandPaletteShortcut(isClient ? () => {} : setPaletteOpen);

  return (
    <div className="relative flex min-h-screen bg-background">
      <div aria-hidden="true" className="ledger-grain pointer-events-none fixed inset-0 z-0 opacity-[0.035] mix-blend-multiply" />
      <IdleTimer />
      {!isClient && <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-primary-foreground"
      >
        Skip to main content
      </a>

      {/* Desktop sidebar (collapsible to an icon rail) — firm users only; clients get a top-bar portal */}
      {!isClient && (
        <aside className={cn("fixed inset-y-0 left-0 z-30 hidden border-r border-shell-border shadow-shell-lg transition-[width] duration-200 lg:block", collapsed ? "w-[68px]" : "w-64")} aria-label="Sidebar">
          <SidebarContent collapsed={collapsed} onToggleCollapsed={toggleCollapsed} groups={navGroups} showPalette={!isClient} workspaceNav={inWorkspace} adminNav={inAdmin} hub={hub} onOpenPalette={() => setPaletteOpen(true)} />
        </aside>
      )}

      {/* Mobile drawer (firm users only) */}
      {navOpen && !isClient && (
        <div id="mobile-nav" className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label="Navigation menu">
          <button type="button" aria-label="Close navigation menu" className="absolute inset-0 bg-foreground/40" onClick={() => setNavOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 border-r border-shell-border">
            <button
              onClick={() => setNavOpen(false)}
              aria-label="Close navigation menu"
              className="absolute right-3 top-4 z-10 text-shell-muted hover:text-shell-text"
            >
              <X className="h-5 w-5" weight="bold" />
            </button>
            <SidebarContent onNavigate={() => setNavOpen(false)} groups={navGroups} showPalette={!isClient} workspaceNav={inWorkspace} adminNav={inAdmin} hub={hub} onOpenPalette={() => { setNavOpen(false); setPaletteOpen(true); }} />
          </aside>
        </div>
      )}

      <BreadcrumbProvider>
        <div className={cn("relative z-10 flex min-w-0 flex-1 flex-col transition-[padding] duration-200", isClient ? "" : (collapsed ? "lg:pl-[68px]" : "lg:pl-64"))}>
          <TopBar onOpenMobileNav={() => setNavOpen(true)} onOpenPalette={() => setPaletteOpen(true)} minimal={isClient} />
          <main id="main-content" className={cn("mx-auto w-full flex-1", isClient ? "max-w-6xl px-6 py-10 sm:px-8 lg:py-14" : "max-w-[1500px] px-5 py-8 sm:px-8 lg:px-10")}>
            <div key={location.pathname} className="rise">{children}</div>
          </main>
          {isClient && <PortalTrustFooter />}
        </div>
      </BreadcrumbProvider>
      <KasaraWidget />
    </div>
  );
}
